/**
 * Profiles React Context.
 *
 * Owns the Profile **list** (metadata only) seeded from the
 * session bootstrap and refreshed imperatively after mutations,
 * plus the per-id document fetch + export helpers. The live
 * *working state* (the applied configuration the rest of the app
 * renders) stays in :class:`PreferencesContext`; this context is
 * about the *named* Profiles a user can apply / edit / export.
 *
 * Increment 4a (ADR-033 §18 step 4, read slice) exposes:
 *   - ``profiles`` — the visible list (Global + own User).
 *   - ``refresh()`` — re-fetch the list from ``GET /api/profiles``.
 *   - ``getDocument(id)`` — fetch a Profile's full document.
 *   - ``exportProfile(id)`` — fetch + download the export wrapper.
 *
 * Apply / save / update land in later increments (they belong to
 * the preferences working-state writer, ADR-033 §4.2).
 *
 * Cross-references:
 *   - REQ-AUTH-065 (list shape; bootstrap delivery).
 *   - REQ-AUTH-066 (single-Profile read).
 *   - REQ-UI-074 (Profiles submenu consumes this list).
 *   - REQ-UI-081 (export).
 *   - ADR-033 §4.2.
 */
import { createContext, useContext } from "react";
import type { ProfileDetail, ProfileSummary } from "@/auth/session-types";

/**
 * The result of an :func:`exportProfile` call. ``status === "ok"``
 * means the document was fetched and the download was triggered;
 * ``status === "error"`` carries a generic, user-facing message
 * (REQ-UI-037) the menu surfaces inline without throwing.
 */
export type ExportProfileResult = { status: "ok" } | { status: "error"; message: string };

export interface ProfilesContextValue {
  /** Visible Profiles for the caller — every Global Profile plus
   *  the caller's own User Profiles, metadata only. Server source
   *  of truth (seeded from bootstrap, re-synced via
   *  :func:`refresh`). */
  profiles: readonly ProfileSummary[];
  /** Re-fetch the list from ``GET /api/profiles`` and replace the
   *  in-memory copy. Called after a mutation lands (later
   *  increments) so the menu reflects the new set. */
  refresh: () => Promise<void>;
  /** Fetch a single Profile's full, forward-migrated document.
   *  Used transiently by Apply / Edit / Export — the document is
   *  never cached in this context (REQ-UI-026 — server state is
   *  fetched on demand, not stashed past render scope). Throws
   *  :class:`ApiError` on failure (404 for a User Profile the
   *  caller doesn't own, per REQ-AUTH-066). */
  getDocument: (profileId: string) => Promise<ProfileDetail>;
  /** Fetch the Profile document and trigger a browser download of
   *  ``{name}.meridian-profile.json`` in the documented export
   *  wrapper (ADR-033 §11.1). Returns a discriminated result so
   *  the caller can surface a failure inline. */
  exportProfile: (profileId: string) => Promise<ExportProfileResult>;
}

export const ProfilesContext = createContext<ProfilesContextValue | null>(null);

/**
 * Read the Profiles list + helpers. Throws if called outside a
 * :class:`ProfilesProvider` — that error indicates a missing
 * provider in the tree, not a runtime fault.
 */
export function useProfiles(): ProfilesContextValue {
  const ctx = useContext(ProfilesContext);
  if (ctx === null) {
    throw new Error(
      "useProfiles() called outside a <ProfilesProvider>. Ensure the component tree is wrapped in main.tsx.",
    );
  }
  return ctx;
}
